import React, { ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import equal from "fast-deep-equal";
import mergeRefs from "merge-refs";
import { usePopper } from "react-popper";

import { ITestableComponentProps } from "@formant/core/model/ITestableComponentProps";
import { Icon, IconName } from "@formant/ui/component/common/Icon";
import { useToggle } from "@formant/ui/utils/useToggle";

import { getPortalElement } from "../../utils/getPortalElement";
import { useOnClickOutside } from "../../utils/hooks/useOnClickOutside";
import { Button } from "./Button";
import { LineButton } from "./LineButton";
import { Menu } from "./Menu";
import { IMenuItem } from "./Menu/IMenuItem";
import * as styles from "./Select.scss";

export interface ISelectProps<T> {
    value?: T;
    options: T[];
    icon?: IconName;
    bottom?: boolean;
    right?: boolean;
    simple?: boolean;
    title?: string;
    titleExtra?: ReactNode;
    responsive?: boolean;
    label?: (value: T) => string;
    chipClassName?: (value: T) => string;
    className?: string;
    lineButtonClassName?: string;
    large?: boolean;
    onChange: (value: T) => void;
    tooltip?: string;
    labelOverride?: string;
    placeholder?: string;
    disabled?: boolean;
}

function defaultLabel<T>(it: T) {
    return `${String(it)}`;
}

function defaultChipClassName() {
    return undefined;
}

export function Select<T extends string | number | undefined>(
    props: ISelectProps<T> & ITestableComponentProps,
) {
    const {
        value,
        options,
        icon,
        bottom,
        right,
        simple,
        title,
        titleExtra,
        responsive,
        className,
        lineButtonClassName,
        large = false,
        onChange,
        labelOverride,
        placeholder,
        disabled,
        label = defaultLabel,
        chipClassName = defaultChipClassName,
    } = props;

    const [open, { off: closeDropdown, toggle: onClicked }] = useToggle(false);

    const selected = options.find(_ => equal(_, value));

    const content =
        selected !== undefined ? label(selected) : labelOverride || title;

    const tooltip = props.tooltip || (title !== content ? title : undefined);

    const clickOutsideRef = useOnClickOutside<HTMLDivElement>(closeDropdown);
    const [refElement, setRefElement] = useState<HTMLElement | null>(null);
    const [popElement, setPopElement] = useState<HTMLDivElement | null>(null);

    const setTargetElement = mergeRefs<HTMLDivElement>(
        setPopElement,
        clickOutsideRef,
    );

    const context = usePopper(refElement, popElement, {
        placement: `${bottom ? "top" : "bottom"}-${right ? "end" : "start"}`,
        modifiers: [
            { name: "flip" },
            { name: "offset", options: { offset: [0, simple ? -24 : 4] } },
        ],
    });

    const menuItems = useMemo(
        () =>
            options.map<IMenuItem<T>>(_ => ({
                value: _,
                label: label(_),
                chipClassName: chipClassName(_),
                selected: equal(_, value),
                onSelected: onChange,
            })),
        [chipClassName, label, onChange, options, value],
    );

    const control = simple ? (
        <Button
            ref={setRefElement}
            onClicked={onClicked}
            data-tooltip={tooltip}
            className={classnames(styles.button, large && styles.large)}
            data-testid={props["data-testid"]}
        >
            {content}
            <Icon name="dropdown" />
        </Button>
    ) : (
        <LineButton
            ref={setRefElement}
            icon={icon}
            onClicked={onClicked}
            data-tooltip={tooltip}
            responsive={responsive}
            toggled={open}
            className={lineButtonClassName}
            data-testid={props["data-testid"]}
            placeholder={placeholder}
            disabled={disabled}
        >
            {content}
        </LineButton>
    );

    const dropdown = open && (
        <div
            ref={setTargetElement}
            {...context.attributes.popper}
            style={context.styles.popper}
        >
            <Menu
                visible={true}
                title={title}
                titleExtra={titleExtra}
                items={menuItems}
                onClose={closeDropdown}
                data-tooltip={tooltip}
                legacy={false}
            />
        </div>
    );

    return (
        <div
            className={classnames(
                styles.select,
                simple && styles.simple,
                className,
            )}
        >
            {control}
            {createPortal(dropdown, getPortalElement())}
        </div>
    );
}
